import { apiClient, ApiResponse } from './client'

export interface CronJob {
  id: string
  name: string
  description: string
  category: string
  endpoint: string
  service: string
}

export const cronJobService = {
  // Subscription Service
  triggerProcessTrialExpirations: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/admin/subscriptions/admin/trigger/process-trial-expirations'
    )
    return response.data
  },

  triggerProcessBillingCycle: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/admin/subscriptions/admin/trigger/process-billing-cycle'
    )
    return response.data
  },

  triggerRetryFailedPayments: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/admin/subscriptions/admin/trigger/retry-failed-payments'
    )
    return response.data
  },

  triggerProcessExpiredGracePeriods: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/admin/subscriptions/admin/trigger/process-expired-grace-periods'
    )
    return response.data
  },

  triggerSendRenewalReminders: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/admin/subscriptions/admin/trigger/send-renewal-reminders'
    )
    return response.data
  },

  triggerSendTrialEndingReminders: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/admin/subscriptions/admin/trigger/send-trial-ending-reminders'
    )
    return response.data
  },

  // Driver Service
  triggerCheckExpiringBackgroundChecks: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/drivers/admin/trigger/check-expiring-background-checks'
    )
    return response.data
  },

  triggerSuspendExpiredDrivers: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/drivers/admin/trigger/suspend-expired-drivers'
    )
    return response.data
  },

  triggerGenerateWeeklyReport: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/drivers/admin/trigger/generate-weekly-report'
    )
    return response.data
  },

  // Finance Service
  triggerProcessPayments: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/finance/admin/trigger/process-payments'
    )
    return response.data
  },

  triggerProcessPayouts: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/finance/admin/trigger/process-payouts'
    )
    return response.data
  },

  // Shipment admin triggers
  triggerMatching: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/admin/shipments/trigger-matching'
    )
    return response.data
  },

  triggerMatchingToday: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/admin/shipments/trigger-matching-today'
    )
    return response.data
  },

  triggerReceiverConfirmation: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/admin/shipments/trigger-receiver-confirmation'
    )
    return response.data
  },

  triggerJobOfferBatch: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/admin/shipments/trigger-job-offer-batch-check'
    )
    return response.data
  },

  clearRouteCache: async () => {
    const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
      '/api/v1/admin/route-cache/clear',
      null,
      { params: { includeSplitSnapshots: false } }
    )
    return response.data
  },

  // Notification Service
  triggerCleanupExpiredNotifications: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/retention/notifications/trigger/cleanup-expired-notifications'
    )
    return response.data
  },

  // Data Retention Services
  triggerChatRetention: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/retention/chat/trigger/execute-retention-policy'
    )
    return response.data
  },

  triggerShipmentRetention: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/retention/shipments/trigger/execute-retention-policy'
    )
    return response.data
  },

  triggerDisputeRetention: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/retention/disputes/trigger/execute-retention-policy'
    )
    return response.data
  },

  triggerFinanceRetention: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/retention/finance/trigger/execute-retention-policy'
    )
    return response.data
  },

  triggerNotificationRetention: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/retention/notifications/trigger/execute-retention-policy'
    )
    return response.data
  },

  triggerLocationRetention: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/retention/location/trigger/execute-retention-policy'
    )
    return response.data
  },

  triggerKycRetention: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/retention/kyc/trigger/execute-retention-policy'
    )
    return response.data
  },

  triggerSubscriptionRetention: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/retention/subscriptions/trigger/execute-retention-policy'
    )
    return response.data
  },
}

/** Admin DLQ retry endpoints — executed in order by `retryAllDeadLetterQueuesSequential`. */
export const DLQ_RETRY_ENDPOINTS = [
  '/api/v1/admin/shipments/dlq/retry',
  '/api/v1/admin/notifications/dlq/retry',
  '/api/v1/admin/location/dlq/retry',
  '/api/v1/admin/vehicles/dlq/retry',
  '/api/v1/admin/subscriptions/dlq/retry',
  '/api/v1/admin/scores/dlq/retry',
  '/api/v1/admin/chat/dlq/retry',
  '/api/v1/admin/kyc/dlq/retry',
  '/api/v1/admin/drivers/dlq/retry',
] as const

export async function retryAllDeadLetterQueuesSequential(): Promise<{
  message: string
}> {
  for (const path of DLQ_RETRY_ENDPOINTS) {
    await apiClient.post<ApiResponse<unknown>>(path)
  }
  return {
    message: `All ${DLQ_RETRY_ENDPOINTS.length} dead-letter retry endpoints completed successfully.`,
  }
}

// Cron job definitions organized by category
export const cronJobCategories = [
  {
    id: 'shipment-jobs',
    name: 'Shipment Jobs',
    description: 'Shipment matching, scheduling, and receiver confirmation tasks',
    jobs: [
      {
        id: 'trigger-matching',
        name: 'Trigger Matching',
        description: 'Run the matching engine for all pending shipments',
        endpoint: 'triggerMatching',
      },
      {
        id: 'trigger-matching-today',
        name: 'Trigger Matching (Today)',
        description: "Run the matching engine for today's shipments only",
        endpoint: 'triggerMatchingToday',
      },
      {
        id: 'trigger-receiver-confirmation',
        name: 'Trigger Receiver Confirmation',
        description:
          'Run the receiver confirmation flow for shipments awaiting confirmation',
        endpoint: 'triggerReceiverConfirmation',
      },
      {
        id: 'trigger-job-offer-batch',
        name: 'Dispatch Job Offer Batch',
        description:
          'Run the job-offer batch notification cycle: dispatch the next wave of pending offers, expire elapsed offers, and advance shipment requests. Only runs on the batch-notification-enabled instance.',
        endpoint: 'triggerJobOfferBatch',
      },
      {
        id: 'clear-route-cache',
        name: 'Clear Route Cache',
        description:
          'Flush the shared Redis matching route-estimate cache (all pods) and this pod’s in-process VRPTW matrix cache, forcing the next matching run to recompute routes. Split snapshots are kept.',
        endpoint: 'clearRouteCache',
      },
    ],
  },
  {
    id: 'subscription',
    name: 'Subscription Management',
    description: 'Manage subscription lifecycle, billing, and reminders',
    jobs: [
      {
        id: 'process-trial-expirations',
        name: 'Process Trial Expirations',
        description: 'Process subscriptions that have reached their trial end date',
        endpoint: 'triggerProcessTrialExpirations',
      },
      {
        id: 'process-billing-cycle',
        name: 'Process Billing Cycle',
        description: 'Process recurring billing for active subscriptions',
        endpoint: 'triggerProcessBillingCycle',
      },
      {
        id: 'retry-failed-payments',
        name: 'Retry Failed Payments',
        description: 'Retry payment processing for failed payment attempts',
        endpoint: 'triggerRetryFailedPayments',
      },
      {
        id: 'process-expired-grace-periods',
        name: 'Process Expired Grace Periods',
        description: 'Handle subscriptions that have exceeded their grace period',
        endpoint: 'triggerProcessExpiredGracePeriods',
      },
      {
        id: 'send-renewal-reminders',
        name: 'Send Renewal Reminders',
        description: 'Send reminder notifications for upcoming subscription renewals',
        endpoint: 'triggerSendRenewalReminders',
      },
      {
        id: 'send-trial-ending-reminders',
        name: 'Send Trial Ending Reminders',
        description: 'Send notifications to users whose trial is ending soon',
        endpoint: 'triggerSendTrialEndingReminders',
      },
    ],
  },
  {
    id: 'driver',
    name: 'Driver Management',
    description: 'Manage driver background checks and compliance',
    jobs: [
      {
        id: 'check-expiring-background-checks',
        name: 'Check Expiring Background Checks',
        description: 'Identify drivers with background checks expiring soon',
        endpoint: 'triggerCheckExpiringBackgroundChecks',
      },
      {
        id: 'suspend-expired-drivers',
        name: 'Suspend Expired Drivers',
        description: 'Suspend drivers with expired background checks',
        endpoint: 'triggerSuspendExpiredDrivers',
      },
      {
        id: 'generate-weekly-report',
        name: 'Generate Weekly Report',
        description: 'Generate weekly background check compliance report',
        endpoint: 'triggerGenerateWeeklyReport',
      },
    ],
  },
  {
    id: 'finance',
    name: 'Finance Processing',
    description: 'Process payments and payouts',
    jobs: [
      {
        id: 'process-payments',
        name: 'Process Payments',
        description: 'Process pending payment transactions',
        endpoint: 'triggerProcessPayments',
      },
      {
        id: 'process-payouts',
        name: 'Process Payouts',
        description: 'Process scheduled payout transactions',
        endpoint: 'triggerProcessPayouts',
      },
    ],
  },
  {
    id: 'notifications',
    name: 'Notifications',
    description: 'Cleanup and maintenance tasks for notifications',
    jobs: [
      {
        id: 'cleanup-expired-notifications',
        name: 'Cleanup Expired Notifications',
        description: 'Remove expired notifications from the system',
        endpoint: 'triggerCleanupExpiredNotifications',
      },
    ],
  },
  {
    id: 'retention',
    name: 'Data Retention',
    description: 'Execute data retention policies across all services',
    jobs: [
      {
        id: 'chat-retention',
        name: 'Chat Data Retention',
        description: 'Execute data retention policy for chat messages',
        endpoint: 'triggerChatRetention',
      },
      {
        id: 'shipment-retention',
        name: 'Shipment Data Retention',
        description: 'Execute data retention policy for shipment requests',
        endpoint: 'triggerShipmentRetention',
      },
      {
        id: 'dispute-retention',
        name: 'Dispute Data Retention',
        description: 'Execute data retention policy for disputes',
        endpoint: 'triggerDisputeRetention',
      },
      {
        id: 'finance-retention',
        name: 'Finance Data Retention',
        description: 'Execute data retention policy for financial records',
        endpoint: 'triggerFinanceRetention',
      },
      {
        id: 'notification-retention',
        name: 'Notification Data Retention',
        description: 'Execute data retention policy for notifications',
        endpoint: 'triggerNotificationRetention',
      },
      {
        id: 'location-retention',
        name: 'Location Data Retention',
        description: 'Execute data retention policy for location tracking data',
        endpoint: 'triggerLocationRetention',
      },
      {
        id: 'kyc-retention',
        name: 'KYC Data Retention',
        description: 'Execute data retention policy for KYC verification data',
        endpoint: 'triggerKycRetention',
      },
      {
        id: 'subscription-retention',
        name: 'Subscription Data Retention',
        description: 'Execute data retention policy for subscription records',
        endpoint: 'triggerSubscriptionRetention',
      },
    ],
  },
]

